import { onAuthStateChanged } from "firebase/auth";
import type { Auth } from "firebase/auth";
import type { Database } from "firebase/database";
import { getUserDetails, normalizeUserProfile } from "~/services/user";

export default defineNuxtPlugin({
  name: "auth-bootstrap",
  dependsOn: ["firebase", "api"],
  setup() {
    const userStore = useUserStore();
    const { userDetails } = storeToRefs(userStore);
    const authStore = useAuthStore();
    const notificationStore = useNotificationStore();
    // Contact store initialized here so contacts are available on any page,
    // not only pages that explicitly call initialize in their own onMounted.
    const contactStore = useContactStore();

    const nuxtApp = useNuxtApp();
    const auth = nuxtApp.$auth as Auth;

    authStore.setReady(false);

    onAuthStateChanged(auth, async (user) => {
      const idToken = useCookie("id_token", {
        sameSite: "lax",
        path: "/",
        secure: process.env.NODE_ENV === "production"
      });

      try {
        if (user) {
          const token: string = await user.getIdToken();
          idToken.value = token;
          authStore.setIdToken(token);
          let response = null;
          try {
            response = await getUserDetails(user.uid);
          } catch (error) {
            console.error("Failed to load user details during auth bootstrap:", error);
            response = null;
          }
          if (response?.user) {
            userStore.changeUser({
              ...normalizeUserProfile(response.user),
              email: user.email,
            } as unknown as User);
          }
          // Start real-time notifications listener for the authenticated user.
          notificationStore.startWatching(user.uid);
          // Initialize contact sync so contacts are available on every page.
          contactStore.initialize(user.uid, nuxtApp.$rtdb as Database).catch(console.error);
        } else {
          userDetails.value = {};
          idToken.value = "";
          authStore.clearIdToken();
          // Tear down the notifications listener when the user signs out.
          notificationStore.stopWatching();
        }
      } finally {
        authStore.setReady(true);
        const hide = () => useSplashScreenStore().hideSplashScreen();
        // onAuthStateChanged can fire before Vue finishes its SSR hydration walk.
        // Hiding the splash mid-hydration makes the client vdom (comment node)
        // diverge from the SSR HTML (div), causing a hydration mismatch warning.
        // Deferring via app:mounted ensures we run after hydration is complete.
        if (nuxtApp.isHydrating) {
          nuxtApp.hook("app:mounted", hide);
        } else {
          hide();
        }
      }
    });
  },
});
